import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search } from "lucide-react";
import { formatDateTime } from "@/lib/api";
import Pagination from "@/components/Pagination";

const ACTION_COLORS = {
  create: "bg-blue-500/15 text-blue-400", approve: "bg-emerald-500/15 text-emerald-400",
  reject: "bg-red-500/15 text-red-400", webhook: "bg-purple-500/15 text-purple-400",
  delete: "bg-red-500/15 text-red-400", update: "bg-yellow-500/15 text-yellow-400",
  create_anticipation: "bg-blue-500/15 text-blue-400", approve_anticipation: "bg-emerald-500/15 text-emerald-400",
  close_month: "bg-orange-500/15 text-orange-400", send_payment: "bg-emerald-500/15 text-emerald-400",
};

export default function AdminAudit({ auditLogs, pagination, onPageChange }) {
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [search, setSearch] = useState("");

  const actions = useMemo(() => [...new Set(auditLogs.map(l => l.action))].sort(), [auditLogs]);
  const entities = useMemo(() => [...new Set(auditLogs.map(l => l.entity_type))].sort(), [auditLogs]);

  const filtered = useMemo(() => auditLogs.filter(log => {
    if (actionFilter !== "all" && log.action !== actionFilter) return false;
    if (entityFilter !== "all" && log.entity_type !== entityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (log.action || "").toLowerCase().includes(q) || (log.entity_type || "").toLowerCase().includes(q) ||
        (log.entity_id || "").toLowerCase().includes(q) || (log.user_id || "").toLowerCase().includes(q);
    }
    return true;
  }), [auditLogs, actionFilter, entityFilter, search]);

  return (
    <div className="space-y-4" data-testid="admin-audit">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="audit-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..." className="pl-9 h-9 bg-black/20 border-white/10" />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger data-testid="audit-action-filter" className="w-[160px] h-9 bg-black/20 border-white/10"><SelectValue placeholder="Ação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as ações</SelectItem>
            {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger data-testid="audit-entity-filter" className="w-[160px] h-9 bg-black/20 border-white/10"><SelectValue placeholder="Entidade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as entidades</SelectItem>
            {entities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="audit-results-count">{filtered.length} de {auditLogs.length} registros</p>
      <Card className="border-white/5">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Data</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Ação</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Entidade</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">ID Entidade</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Usuário</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="audit-empty-state">Nenhum registro encontrado</TableCell></TableRow>
              ) : filtered.map((log) => (
                <TableRow key={log.id} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="text-muted-foreground text-sm">{formatDateTime(log.created_at)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ACTION_COLORS[log.action] || "bg-white/10 text-foreground"}`}>{log.action}</span>
                  </TableCell>
                  <TableCell className="font-medium">{log.entity_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{log.entity_id?.slice(0, 8)}...</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{log.user_id === "system" ? "Sistema" : log.user_id?.startsWith("api:") ? "API" : log.user_id?.slice(0, 8)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages}
        total={pagination.total} pageSize={pagination.page_size} onPageChange={onPageChange} />}
    </div>
  );
}
